import _                                                                      from 'underscore';
import classNames                                                             from 'classnames';
import React, { FC, useEffect, useRef, useState }                             from 'react';
import { Button, Divider, Form, Header, Icon, List, Segment, SemanticCOLORS } from 'semantic-ui-react';
import { Formik, FormikConfig }                                               from 'formik';
import * as Yup                                                               from 'yup';
import { Cell, Grid }                                                         from 'styled-css-grid';
import { useQuery }                                                           from '@apollo/react-hooks';

import { Align, CorvalProjectActivity, CorvalProjectSubObjective, SystemEvent, Uuid } from '@src/types';

import { ProjectActivityStatusChangeCallback, ProjectActivityStatusChangeDelta, useAuditRecords, useFeatureFlags, useProjectActivities, useSystemToasts, useWindowMeasurements } from '@src/hooks';

import { DecisionEngineQueries, SystemQueries } from '@src/queries';
import { TimeService }                          from '../../../../../services';
import { runIfFunction }                                         from '../../../../../util/FunctionalUtility';
import { queriesHaveData, queriesLoading }                       from '../../../../../store/_apolloUtil';
import { ACTIVITY_STATUS }                                       from '../../../../../constants/ProductEnums';
import { DATES }                                                 from '../../../../../constants/FormatStringEnums';
import { AUDIT_RECORD_TYPE }                                     from '../../../../../constants/SystemRecordEnums';
import { useProjectTermDictionary }                              from '../../../../../hooks/useTermDictionary';
import { CenteredContentContainer, CollapsibleContentContainer } from '../../../../containers';
import { QueryDialogProps, QueryOpDialog }                       from '../../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                  from '../../../../components/modals/BasicDialogContainer';
import { MultipleInputArrayField }                               from '../../../../components/forms/containers/MultipleInputArrayField';
import { CompletionStatusDropdown }                              from '../../../../components/forms/dropdowns/CompletionStatusDropdown';
import { OrganizationUsersWithBusinessFunctions }                from '../../../../components/user/OrganizationUsersWithBusinessFunctions';
import { FinePrintMessage }                                      from '../../../../components/util/messaging/FinePrintMessage';
import { FieldLabel }                                            from '../../../../components/forms/formik/FieldLabel';
import { HumanizedDateRangeLabel }                               from '../../../../components/labels/HumanizedDateRangeLabel';
import { AuditMemoList }                                         from '../../../../components/audits/AuditMemoList';
import { CorvalRecommendedTag }                                  from '../../../../components/product/CorvalRecommendedTag';
import { TextLink }                                              from '../../../../components/display/TextLink';
import { LastUpdatedAuditTag }                                   from '../../../../components/audits/LastUpdatedAuditTag';
import { ProjectActivityDetails }                                from '../../activities/ProjectActivityDetails';
import { IncompleteParentActivitiesWarning }                     from '../../activities/ProjectActivityParentProgressDetails';
import { ActivityRecurrenceSubHeader }                           from '../../activities/ActivityRecurrenceSubHeader';
import { OrganizationUserBusinessFunctionSupportListItem }       from "../../../../components/user/OrganizationUserBusinessFunctionSupportListItem";
import { FocusedContentWithTeamSupportList }                     from "../../../../components/user/FocusedContentWithTeamSupportList";
import { getFieldWithProjectVersionIdAccessor }                  from "@src/util";


export interface ObjectiveProgressionUpdateDialogProps {
    id: Uuid,
    projectId: Uuid,
    projectVersionId?: Uuid,
    organizationId: Uuid,

    onUpdateComplete?: ProjectActivityStatusChangeCallback,

    onUpdateError?( err ): void

    onUpdateCanceled?( e: SystemEvent ): void
}

type ObjectiveProgressionUpdateDialogFormValues = {
    projectActivities: CorvalProjectActivity[]
}

type ObjectiveProgressionUpdateDialogFormConfig = FormikConfig<ObjectiveProgressionUpdateDialogFormValues>

const defaultProps: ObjectiveProgressionUpdateDialogProps = {
    id:               null,
    projectId:        null,
    projectVersionId: null,
    organizationId:   null,
    onUpdateComplete: null,
    onUpdateError:    null,
};

const ObjectiveProgressionUpdateDialog: FC<ObjectiveProgressionUpdateDialogProps> = ( props ) => {

    let toastActions                                                        = useSystemToasts(),
        { getAuditRecordTypes }                                             = useAuditRecords(),
        { getTerm }                                                         = useProjectTermDictionary(),
        { getUpdateProjectActivityInputData, getProjectActivityStatusInfo } = useProjectActivities(),
        [ isLoading, setIsLoading ]                                         = useState( false ),
        [ isProcessing, setIsProcessing ]                                   = useState( false ),
        [ projectSubObjective, setProjectSubObjective ]                     = useState<CorvalProjectSubObjective>( null ),
        [ originalStatuses, setOriginalStatuses ]                           = useState<Record<Uuid, ACTIVITY_STATUS>>( {} ),
        formikRef                                                           = useRef( {} as any ),
        featureFlags                                                        = useFeatureFlags(),
        { maxDialogHeight }                                                 = useWindowMeasurements();

    // when window height changes..
    useEffect( () => {

        // console.log( { height, maxDialogHeight } )

    }, [ maxDialogHeight ] );

    let getProjectSubObjectiveReq = useQuery( DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES( `
		id
		startTimeUtc
		endTimeUtc
		project{
			id
			name
		}
		subObjective{
			id
			displayName
			description
			category{ id displayName }
			subCategory{ id displayName }
			isRecommended( projectId: "${ props.projectId }" )
		}
		${ props.projectVersionId ? getFieldWithProjectVersionIdAccessor( props.projectVersionId, 'staticProjectActivities' ) : 'projectActivities' }{
			displayName
			startTimeUtc
			endTimeUtc
			recurrenceType
			isRecurrence
			${ DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS_VITAL }
			${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
			activity{
				id
				displayName
				description
				businessFunction
				defaultCriticality
				isRequired
				parentActivities{
					parentActivity{
						id
						displayName
					}
					parentCompletionRequired
					parentSchedulingEnforced
				}
			}
		}
	` ), {
        variables: {
            projectId: props.projectId,
            where:     {
                subObjectiveId: {
                    eq: props.id
                }
            }
        }
    } );

    let dataIsLoading = queriesLoading( getProjectSubObjectiveReq ) || _.isNull( projectSubObjective );

    useEffect( () => {

        if( !queriesLoading( getProjectSubObjectiveReq ) &&
            queriesHaveData( getProjectSubObjectiveReq ) &&
            !isProcessing
        ){

            let projectSubObjective = _.first( getProjectSubObjectiveReq.data.projectSubObjectives ) ?? null;

            let originalStatuses = _.chain( projectSubObjective.projectActivities )
                                    .groupBy( 'id' )
                                    .mapObject( group => _.first( group ).completionState )
                                    .value();

            // console.log( 'setting originalStatuses' );
            // console.table( originalStatuses );

            setOriginalStatuses( originalStatuses );
            setProjectSubObjective( projectSubObjective );
        }

    }, [ getProjectSubObjectiveReq ] );

    useEffect( () => {

        // console.log( 'originalStatuses updated' );
        // console.table( originalStatuses );

    }, [ originalStatuses ] );

    let getGraphArgs = () => {

        if( !projectSubObjective || !( formikRef.current?.values ) ) return {};

        let requiredFields = [
            'id',
            'completionState',
            'recurrenceMilestonesOverride'
        ];

        let originalActivities = projectSubObjective.projectActivities.map( pa => _.pick( pa, requiredFields ) ),
            projectActivities  = formikRef.current?.values?.projectActivities ?? [],
            statusUpdates      = _.chain( projectActivities )
                                  .map( pa => {

                                      let originalActivity = _.findWhere( originalActivities, { id: pa.id } ) as CorvalProjectActivity ?? null,
                                          stateIsDifferent = originalActivity && ( originalActivity.completionState !== pa.completionState );

                                      return stateIsDifferent ? getUpdateProjectActivityInputData( pa ) : null;
                                  } )
                                  .compact()
                                  .value();

        return ( {
            variables: {
                input: {
                    projectId:         props.projectId,
                    projectActivities: statusUpdates
                }
            }
        } );
    };

    let handleOnRequestSubmit: QueryDialogProps['onSubmit'] = () => {

        setIsLoading( true );
        setIsProcessing( true );
    };

    let handleOnRequestComplete: QueryDialogProps['onComplete'] = ( res ) => {

        setIsLoading( false );
        setIsProcessing( false );

        if( res ){

            let results = res.updateProjectActivities;

            toastActions.success( 'Objective Progress Updated!' );

            // Craft object deltas with status changes indicated
            let deltaObjects = results.map( ( { id, completionState } ) => {

                let oldValue = originalStatuses[ id ],
                    newValue = completionState;

                return ( {
                    projectActivityId: id,
                    oldValue,
                    newValue,
                } as ProjectActivityStatusChangeDelta );
            } );

            console.table( deltaObjects );

            // Run post update method w/ delta objects
            runIfFunction( props.onUpdateComplete, deltaObjects );

            // Refetch the set, updating cache models
            getProjectSubObjectiveReq.refetch();
        }
    };

    let handleOnRequestError: QueryDialogProps['onError'] = async ( error ) => {

        setIsLoading( false );

        toastActions.error( 'Objective Progress Not Updated!', error.message ?? 'Something prevented Corval from updating the progress of this objective. Please try again.' );

        runIfFunction( props.onUpdateError, formikRef.current.values );
    };

    let handleOnRequestCancelled: QueryDialogProps['onClose'] = ( e ) => {

        setIsLoading( false );

        runIfFunction( props.onUpdateCanceled, e );
    };

    let handleSetAllStatuses = ( status: ACTIVITY_STATUS ) => {

        let projectActivities = formikRef.current.values.projectActivities.map( projectActvity => ( { ...projectActvity, completionState: status } ) );

        formikRef.current.setValues( {
            projectActivities
        } );
    };

    const getMappedRequestData = (): ObjectiveProgressionUpdateDialogFormValues => {

        let data = {
            projectActivities: []
        };

        if( !_.isNull( projectSubObjective ) ){

            // return in chronological order
            data.projectActivities = _.sortBy( projectSubObjective.projectActivities, ( pa: CorvalProjectActivity ) => TimeService.ISOtoFormat( pa.startTimeUtc, DATES.YEARMONTHDAY_MOMENT ) );
        }

        return data;
    };

    let formProps: ObjectiveProgressionUpdateDialogFormConfig = {
        innerRef:           formikRef,
        isInitialValid:     false,
        enableReinitialize: true,
        initialValues:      getMappedRequestData(),
        validationSchema:   Yup.object( {
            projectActivities: Yup.array()
        } ),
        onSubmit:           null,
    };

    return (

        <Formik { ...formProps }>

            { formik => {

                let relevantBusinessFunctions = dataIsLoading ? [] : _.chain( projectSubObjective.projectActivities ).pluck( 'activity' ).pluck( 'businessFunction' ).flatten().uniq().value();

                return (

                    <>

                        <QueryOpDialog
                            showOperationInTransitMessage={ false }
                            operationDisabled={ isLoading || !formik.isValid }
                            mutation={ DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES( `id startTimeUtc endTimeUtc completionState` ) }
                            graphArgs={ getGraphArgs() }
                            onSubmit={ handleOnRequestSubmit }
                            onComplete={ handleOnRequestComplete }
                            onError={ handleOnRequestError }
                            onClose={ handleOnRequestCancelled }
                            cancelLabel={ 'Close' }
                            submitLabel={ 'Update Progress' }
                            successToastLabel={ null }
                            className={ 'no-padding' }
                            alignControls={ Align.CENTER }>

                            <BasicDialogContainer title={ 'Objective Progression' } contentPadding={ null }>

                                { dataIsLoading
                                  ? (
                                      /* The modal dimmer makes this perma-white text, so construct a different loader than normal*/
                                      <CenteredContentContainer className={ 'padding-lg' }>
                                          <><Icon loading name="circle notch"/> Retrieving Objective Data...</>
                                      </CenteredContentContainer>

                                  ) : ( <Form>

                                            <FocusedContentWithTeamSupportList
                                                contentTitle={ projectSubObjective.subObjective.displayName }
                                                contentDescription={ projectSubObjective.subObjective.description }
                                                contentIcon={ { name: 'bullseye', color: 'teal' } }
                                                focusedContentMaxHeight={ `${ maxDialogHeight }px` }
                                                contentIndicators={ (

                                                    <List className={ 'no-padding' }>
                                                        <List.Item>
                                                            <HumanizedDateRangeLabel
                                                                startDate={ TimeService.ISOtoMoment( projectSubObjective.startTimeUtc ) }
                                                                endDate={ TimeService.ISOtoMoment( projectSubObjective.endTimeUtc ) }
                                                                withSummaryLabel={ false }
                                                            />
                                                        </List.Item>
                                                        { projectSubObjective.subObjective.isRecommended && (
                                                            <List.Item>
                                                                <CorvalRecommendedTag/>
                                                            </List.Item>
                                                        ) }
                                                    </List>

                                                ) }
                                                teamSupportListContent={ (

                                                    /* Team List */
                                                    <OrganizationUsersWithBusinessFunctions
                                                        projectId={ props.projectId }
                                                        organizationId={ props.organizationId }
                                                        businessFunctions={ relevantBusinessFunctions }
                                                        listChildComponent={ ( props ) => (
                                                            <OrganizationUserBusinessFunctionSupportListItem
                                                                user={ props.user }
                                                                emailSubject={ `Corval - ${ projectSubObjective.project.name }: Re: Activities in objective "${ projectSubObjective.subObjective.displayName }"` }
                                                            />
                                                        ) }
                                                    />

                                                ) }
                                                focusedContentHeaderContent={ (

                                                    <Grid columns={ `20px auto auto` }>
                                                        <Cell width={ 2 }>

                                                            <FieldLabel>Activities in this objective</FieldLabel>
                                                            <FinePrintMessage>Below are all the activities available on your map, listed with their current progress.</FinePrintMessage>

                                                        </Cell>
                                                        <Cell>

                                                            <List horizontal>

                                                                <List.Item className={ classNames( { 'opacity--0': !formik.dirty } ) }>

                                                                    <Button basic
                                                                            size={ 'mini' }
                                                                            icon={ 'undo' }
                                                                            content={ 'Reset' }
                                                                            onClick={ () => formik.resetForm() }
                                                                            disabled={ !formik.dirty }/>

                                                                </List.Item>
                                                                <List.Item>

                                                                    <Button basic
                                                                            size={ 'mini' }
                                                                            icon={ { name: 'circle', color: 'teal' } }
                                                                            content={ 'Start All' }
                                                                            onClick={ () => handleSetAllStatuses( ACTIVITY_STATUS.INCOMPLETE ) }/>

                                                                    <Button basic
                                                                            size={ 'mini' }
                                                                            icon={ { name: 'check circle', color: 'green' } }
                                                                            content={ 'Complete All' }
                                                                            onClick={ () => handleSetAllStatuses( ACTIVITY_STATUS.COMPLETE ) }/>

                                                                </List.Item>
                                                            </List>

                                                        </Cell>
                                                    </Grid>

                                                ) }
                                                focusedContentMainContent={ (

                                                    <MultipleInputArrayField<CorvalProjectActivity>
                                                        name={ 'projectActivities' }
                                                        label={ null }
                                                        descriptionText={ null }
                                                        addDisabled={ true }
                                                        addVisible={ false }
                                                        removeDisabled={ true }
                                                        listContainerClasses={ 'no-padding' }
                                                        draggableRemovableContainerProps={ {
                                                            contentContainerClasses: '',
                                                            hasRemoveArea:           false,
                                                            hasHandles:              false,
                                                        } }
                                                        renderField={ ( item, index ) => {

                                                            const { isRecurrence }                        = item,
                                                                  { statusColor, statusIcon, isComplete } = getProjectActivityStatusInfo( item ),
                                                                  records                                 = getAuditRecordTypes( item.auditEntries, [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_STATUS_CHANGED ], 'newest', [ item.id ] ),
                                                                  hasAuditEntries                         = ( records.length > 0 ),
                                                                  hasRecurrences                          = !_.isNull( item.recurrenceType );

                                                            return (
                                                                <Segment color={ statusColor as SemanticCOLORS }>

                                                                    <CollapsibleContentContainer
                                                                        borderless={ true }
                                                                        contentClasses={ 'no-padding' }
                                                                        headerComponent={ ( headerProps ) => (

                                                                            <Grid columns={ `30px auto 200px` }
                                                                                  columnGap={ '20px' }
                                                                                  rows={ 'repeat(5,auto)' }
                                                                                  rowGap={ '5px' }
                                                                                  areas={ [
                                                                                      '. recurrenceLabel .',
                                                                                      'icon name statusControl',
                                                                                      'icon dates statusControl',
                                                                                      '. desc desc',
                                                                                      '. messaging messaging',
                                                                                  ] }>

                                                                                { ( hasRecurrences || isRecurrence ) && (
                                                                                    <Cell area={ 'recurrenceLabel' }>
                                                                                        { isRecurrence && <ActivityRecurrenceSubHeader/> }
                                                                                        {/*{ hasRecurrences && <ActivityRecurrenceSubHeader isRecurrence={ false }/> }*/ }
                                                                                    </Cell>
                                                                                ) }

                                                                                {/* Icon signifer */ }
                                                                                <Cell center area={ 'icon' }>
                                                                                    <Icon name={ statusIcon } color={ statusColor as SemanticCOLORS } size={ 'large' }/>
                                                                                </Cell>

                                                                                {/* Activity name */ }
                                                                                <Cell area={ 'name' }>
                                                                                    <Header as={ 'h5' } onClick={ headerProps.onClick } className={ 'cursor--pointer no-margin-bottom' }>{ item.displayName }</Header>
                                                                                </Cell>

                                                                                {/* Completion state control */ }
                                                                                <Cell area={ 'statusControl' }>
                                                                                    <CompletionStatusDropdown
                                                                                        loading={ isLoading }
                                                                                        name={ `projectActivities[${ index }].completionState` }
                                                                                        omit={ item.activity.isRequired ? [ ACTIVITY_STATUS.EXCLUDED ] : [] }
                                                                                    />
                                                                                </Cell>

                                                                                {/* Start / End Date */ }
                                                                                <Cell area={ 'dates' } className={ 'padding-xs-top padding-sm-bottom' }>
                                                                                    <HumanizedDateRangeLabel
                                                                                        singleLabel={ true }
                                                                                        withSummaryLabel={ false }
                                                                                        relativeLanguage={ false }
                                                                                        startDate={ TimeService.ISOtoMoment( item.startTimeUtc ) }
                                                                                        endDate={ TimeService.ISOtoMoment( item.endTimeUtc ) }
                                                                                    />
                                                                                </Cell>

                                                                                {/* Description */ }
                                                                                <Cell area={ 'desc' }>

                                                                                    <p className={ 'text--fine-print' }>{ item.activity.description } <TextLink onClick={ headerProps.onClick }>Review Details</TextLink></p>

                                                                                </Cell>

                                                                                {/* Status and messages */ }
                                                                                <Cell area={ 'messaging' }>

                                                                                    <List horizontal>

                                                                                        {/* Auditing entry */ }
                                                                                        { hasAuditEntries && (
                                                                                            <List.Item>
                                                                                                <AuditMemoList
                                                                                                    organizationId={ props.organizationId }
                                                                                                    records={ records }
                                                                                                    types={ [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_STATUS_CHANGED ] }
                                                                                                    timeBoundary={ 'newest' }
                                                                                                    entityIds={ [ item.id ] }
                                                                                                    actionDesc={ '' }
                                                                                                    actionVerb={ isComplete ? 'completed' : 'updated' }
                                                                                                    auditSubject={ isComplete ? 'this activity' : `this activity's status` }
                                                                                                    wrapperComponent={ ( props ) => <LastUpdatedAuditTag { ...props } /> }
                                                                                                />
                                                                                            </List.Item>
                                                                                        ) }

                                                                                        {/* Parent Activity information */ }
                                                                                        {/* If we have unfinished parents when marking this activity complete, display those in chronological order. */ }
                                                                                        { isComplete && (
                                                                                            <List.Item>
                                                                                                <IncompleteParentActivitiesWarning
                                                                                                    projectId={ props.projectId }
                                                                                                    projectActivityId={ item.id }
                                                                                                />
                                                                                            </List.Item>
                                                                                        ) }

                                                                                    </List>
                                                                                </Cell>

                                                                            </Grid>
                                                                        ) }>

                                                                        <Grid columns={ `30px auto 200px` } columnGap={ '20px' } rowGap={ '20px' }>
                                                                            <Cell width={ 2 } left={ 2 }>

                                                                                <Divider/>

                                                                                <ProjectActivityDetails
                                                                                    id={ item.id }
                                                                                    projectId={ props.projectId }
                                                                                    organizationId={ props.organizationId }
                                                                                    segmentProps={ { className: 'no-padding' } }
                                                                                    listProps={ { divided: true, relaxed: true } }
                                                                                    showHeader={ false }
                                                                                    showProgress={ false }
                                                                                    showTooltips={ false }
                                                                                    showDescription={ false }
                                                                                    showSupportingObjective={ false }
                                                                                    showCriticality={ featureFlags.enableProjectActivityCriticalityViewsAndTools }
                                                                                    canEditRecurrences={ false }
                                                                                    canEditTiming={ false }
                                                                                    canEditDisplayOptions={ false }
                                                                                    includeObjectiveLinks={ false }
                                                                                    includeChildDependencyLinks={ false }
                                                                                    includeParentDependencyLinks={ false }
                                                                                />

                                                                            </Cell>
                                                                        </Grid>

                                                                    </CollapsibleContentContainer>
                                                                </Segment>
                                                            );

                                                        } }/>

                                                ) }/>

                                        </Form>
                                  ) }

                            </BasicDialogContainer>

                        </QueryOpDialog>

                    </>
                );
            } }
        </Formik>
    );
};

ObjectiveProgressionUpdateDialog.defaultProps = defaultProps;

export { ObjectiveProgressionUpdateDialog };
